import { useState, useEffect, useRef } from 'react'
import { cn } from 'src/shared/components/ui/utils'
import { useVirtualizer } from '@tanstack/react-virtual'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import { ProductCard } from 'src/shared/components/ProductGrid/ProductCard'
import { TabNavigation } from 'src/shared/components/TabNavigation'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { ProductItem } from '../message_components/ProductSelectionMessage/types'
import { useProductsByCategoryName } from './hooks'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const COLS = 4
const ROW_HEIGHT_ESTIMATE = 400
const GAP = 16

interface ProductOverlayProps {
  products: ProductItem[]
  quote_id: number
  product_categories?: string[]
  onClose: () => void
  orderPanelOpen: boolean
  /** Portal to body and cover the full viewport (e.g. request chat sidebar). */
  fullscreen?: boolean
}

export const ProductOverlay = ({
  products,
  quote_id,
  product_categories = [],
  onClose,
  orderPanelOpen,
  fullscreen = false,
}: ProductOverlayProps) => {
  const [activeTab, setActiveTab] = useState(product_categories?.[0] ?? '')
  const { t } = useLocalization()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const categoryName = activeTab || null
  const { productItems: categoryProducts, isLoading } =
    useProductsByCategoryName(categoryName)

  const tabs = product_categories.map((cat) => ({
    id: cat,
    label: `${t('All in', 'Alla i')} "${cat}"`,
  }))

  const displayProducts =
    product_categories.length === 0 ? products : categoryProducts

  const scrollRef = useRef<HTMLDivElement>(null)
  const rowCount = Math.ceil(displayProducts.length / COLS)

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE + GAP,
    overscan: 2,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const totalHeight = rowVirtualizer.getTotalSize()

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={cn(
        'flex justify-center w-full',
        fullscreen ? 'fixed inset-0 z-[100]' : 'absolute inset-0 z-50'
      )}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/20" aria-hidden />

      <div
        className={cn(
          'absolute flex justify-center',
          fullscreen
            ? 'inset-2 md:inset-4'
            : orderPanelOpen
              ? 'inset-2'
              : 'inset-y-2 inset-x-0 max-w-6xl mx-auto'
        )}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="relative w-full h-full bg-white rounded-[20px] border border-gray-200 shadow-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <Column className="h-full p-3 overflow-hidden gap-4">
            <Row className="items-center justify-between">
              <Button onClick={onClose} className="mr-4" variant="ghost">
                <Row className="items-center gap-1.5">
                  {t('Close', 'Stäng')}
                  <X className="w-3 h-3" />
                </Row>
              </Button>

              {tabs.length > 0 && (
                <TabNavigation
                  tabs={tabs}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
              )}
            </Row>

            {isLoading ? (
              <div className="flex-1 flex items-center justify-center min-h-0">
                <p className="text-muted-foreground">
                  {t('Fetching products...', 'Laddar produkter...')}
                </p>
              </div>
            ) : displayProducts.length === 0 ? (
              <div className="flex-1 flex items-center justify-center min-h-0">
                <p className="text-muted-foreground">
                  {t(
                    'No products in this category',
                    'Inga produkter i den här kategorin'
                  )}
                </p>
              </div>
            ) : (
              <div
                ref={scrollRef}
                className="overflow-y-auto flex-1 min-h-0 pr-2"
              >
                <div
                  style={{
                    height: `${totalHeight}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {virtualRows.map((virtualRow) => {
                    const startIdx = virtualRow.index * COLS
                    const rowProducts = displayProducts.slice(
                      startIdx,
                      startIdx + COLS
                    )
                    return (
                      <div
                        key={virtualRow.key}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualRow.start}px)`,
                          display: 'grid',
                          gridTemplateColumns: `repeat(${COLS}, 1fr)`,
                          gap: GAP,
                        }}
                      >
                        {rowProducts.map((product_item) => (
                          <div
                            className="w-full min-w-0 p-1"
                            key={product_item.product_id}
                          >
                            <ProductCard
                              product_item={product_item}
                              isChat={true}
                              addToCurrentQuoteId={quote_id}
                            />
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </Column>
        </motion.div>
      </div>
    </motion.div>
  )
}
